import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { X, User as UserIcon, Mail, Shield, Calendar, Clock, CheckCircle, XCircle, AlertCircle, Activity } from 'lucide-react';
import ResetPasswordModal from './ResetPasswordModal';
import { apiClient } from '../../services/api';

interface UserViewModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: {
        id: string;
        firstName: string;
        lastName: string;
        email: string;
        role: string;
        isActive: boolean;
        organisationId: string;
        invitationToken: string | null;
        invitationExpiry: Date | null;
        invitedBy: string | null;
        lastLogin: Date | null;
        createdAt: Date;
        updatedAt: Date;
    } | null;
    currentUserId: string;
    onEdit?: () => void;
    onDeactivate?: () => void;
    onReactivate?: () => void;
    onRefresh?: () => void;
}

const UserViewModal: React.FC<UserViewModalProps> = ({
    isOpen,
    onClose,
    user,
    currentUserId,
    onEdit,
    onDeactivate,
    onReactivate,
    onRefresh
}) => {
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [activities, setActivities] = useState<any[]>([]);
    const [loadingActivities, setLoadingActivities] = useState(false);

    const loadUserActivity = useCallback(async () => {
        if (!user) return;

        setLoadingActivities(true);
        try {
            const response = await apiClient.getUserActivity(user.id, 20);
            setActivities(response.data || []);
        } catch (error) {
            console.error('Failed to load user activity:', error);
            setActivities([]);
        } finally {
            setLoadingActivities(false);
        }
    }, [user]);

    // Fetch user activity when modal opens
    useEffect(() => {
        if (isOpen && user?.id) {
            loadUserActivity();
        }
    }, [isOpen, user, loadUserActivity]);

    if (!isOpen || !user) return null;

    const formatDate = (dateString: string | Date | null): string => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDateOnly = (dateString: string | Date | null): string => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatRoleName = (role: string): string => {
        const roleNames: { [key: string]: string } = {
            'super_admin': 'Super Admin',
            'admin': 'Admin',
            'manager': 'Manager',
            'compliance': 'Compliance',
            'user': 'User'
        };
        return roleNames[role] || role;
    };

    const getRoleBadgeClass = (role: string): string => {
        switch (role) {
            case 'super_admin':
                return 'bg-purple-100 text-purple-800 border-purple-200';
            case 'admin':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'manager':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'compliance':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'user':
                return 'bg-gray-100 text-gray-800 border-gray-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getRoleDescription = (role: string): string => {
        const descriptions: { [key: string]: string } = {
            'super_admin': 'Platform-level access across all organisations',
            'admin': 'Full access to manage users, drivers, and perform all operations',
            'manager': 'Can manage drivers and perform licence checks',
            'compliance': 'Read-only access to view drivers and check results',
            'user': 'Can perform licence checks and view assigned drivers'
        };
        return descriptions[role] || 'Standard user permissions';
    };

    const getUserStatus = () => {
        if (!user.isActive) {
            return {
                label: 'Inactive',
                icon: XCircle,
                color: 'text-red-800',
                bgColor: 'bg-red-100',
                borderColor: 'border-red-200'
            };
        }

        if (user.invitationToken && user.invitationExpiry) {
            const now = new Date();
            const expiry = new Date(user.invitationExpiry);
            if (expiry > now) {
                return {
                    label: 'Invitation Pending',
                    icon: Clock,
                    color: 'text-yellow-800',
                    bgColor: 'bg-yellow-100',
                    borderColor: 'border-yellow-200'
                };
            } else {
                return {
                    label: 'Invitation Expired',
                    icon: AlertCircle,
                    color: 'text-orange-800',
                    bgColor: 'bg-orange-100',
                    borderColor: 'border-orange-200'
                };
            }
        }

        return {
            label: 'Active',
            icon: CheckCircle,
            color: 'text-green-800',
            bgColor: 'bg-green-100',
            borderColor: 'border-green-200'
        };
    };

    const formatActionType = (action: string): string => {
        const actionMap: { [key: string]: string } = {
            'USER_CREATED': 'User Created',
            'USER_UPDATED': 'Profile Updated',
            'USER_DELETED': 'User Deactivated',
            'USER_ACTIVATED': 'User Reactivated',
            'USER_ROLE_CHANGED': 'Role Changed',
            'USER_INVITED': 'User Invited',
            'PASSWORD_RESET': 'Password Reset',
            'PASSWORD_CHANGED': 'Password Changed',
            'LOGIN_SUCCESS': 'Login Successful',
            'LOGIN_FAILED': 'Login Failed',
            'LOGOUT': 'Logged Out',
            'ACCOUNT_LOCKED': 'Account Locked',
            'ACCOUNT_UNLOCKED': 'Account Unlocked'
        };
        return actionMap[action] || action;
    };

    const formatRelativeTime = (dateString: string | Date): string => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
        if (diffInSeconds < 172800) return 'Yesterday';
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;

        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const status = getUserStatus();
    const StatusIcon = status.icon;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex justify-between items-start p-4 sm:p-6 border-b border-gray-200">
                    <div className="flex items-start space-x-3 sm:space-x-4 min-w-0 flex-1">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: 'rgba(105, 190, 40, 0.1)' }}>
                            <span className="text-lg sm:text-2xl font-bold" style={{ color: '#69BE28' }}>
                                {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                            </span>
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                                {user.firstName} {user.lastName}
                            </h2>
                            <p className="text-sm sm:text-base text-gray-600 truncate">{user.email}</p>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:space-x-2 mt-2">
                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border self-start ${getRoleBadgeClass(user.role)}`}>
                                    {formatRoleName(user.role)}
                                </span>
                                <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full border self-start ${status.bgColor} ${status.color} ${status.borderColor}`}>
                                    <StatusIcon className="w-3 h-3 mr-1" />
                                    {status.label}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1 ml-2 flex-shrink-0"
                    >
                        <X className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6 space-y-6">
                    {/* Personal Information */}
                    <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 flex items-center">
                            <UserIcon className="w-5 h-5 mr-2" style={{ color: '#69BE28' }} />
                            Personal Information
                        </h3>
                        <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">First Name</p>
                                    <p className="text-sm font-medium text-gray-900">{user.firstName}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">Last Name</p>
                                    <p className="text-sm font-medium text-gray-900">{user.lastName}</p>
                                </div>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Email Address</p>
                                <p className="text-sm font-medium text-gray-900 font-mono">{user.email}</p>
                            </div>
                        </div>
                    </div>

                    {/* Role & Permissions */}
                    <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 flex items-center">
                            <Shield className="w-5 h-5 mr-2" style={{ color: '#69BE28' }} />
                            Role & Permissions
                        </h3>
                        <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-3">
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Current Role</p>
                                <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full border ${getRoleBadgeClass(user.role)}`}>
                                    {formatRoleName(user.role)}
                                </span>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Permissions</p>
                                <p className="text-sm text-gray-700">{getRoleDescription(user.role)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Account Information */}
                    <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 flex items-center">
                            <Calendar className="w-5 h-5 mr-2" style={{ color: '#69BE28' }} />
                            Account Information
                        </h3>
                        <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">Account Status</p>
                                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full border ${status.bgColor} ${status.color} ${status.borderColor}`}>
                                        <StatusIcon className="w-3 h-3 mr-1" />
                                        {status.label}
                                    </span>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">Last Login</p>
                                    <p className="text-sm font-medium text-gray-900">{formatDate(user.lastLogin)}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">Account Created</p>
                                    <p className="text-sm font-medium text-gray-900">{formatDateOnly(user.createdAt)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">Last Updated</p>
                                    <p className="text-sm font-medium text-gray-900">{formatDateOnly(user.updatedAt)}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Invitation Information (if pending) */}
                    {user.invitationToken && (
                        <div>
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 flex items-center">
                                <Mail className="w-5 h-5 mr-2" style={{ color: '#69BE28' }} />
                                Invitation Status
                            </h3>
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 space-y-2">
                                <div className="flex items-start">
                                    <Clock className="w-4 h-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-yellow-900">Invitation Pending</p>
                                        <p className="text-xs text-yellow-700 mt-1">
                                            This user has been invited but hasn't accepted the invitation yet.
                                        </p>
                                    </div>
                                </div>
                                {user.invitationExpiry && (
                                    <div className="pt-2 border-t border-yellow-200">
                                        <p className="text-xs text-yellow-700">
                                            <strong>Expires:</strong> {formatDate(user.invitationExpiry)}
                                        </p>
                                    </div>
                                )}
                                <div className="pt-2 border-t border-yellow-200 flex flex-wrap gap-2">
                                    <button
                                        onClick={async () => {
                                            if (!window.confirm(`Resend invitation to ${user.firstName} ${user.lastName}?\n\nA new invitation email will be sent with a fresh 48-hour expiry.`)) {
                                                return;
                                            }
                                            try {
                                                await apiClient.resendInvitation(user.id);
                                                toast.success('Invitation resent successfully!');
                                                if (onRefresh) onRefresh();
                                                onClose();
                                            } catch (error: any) {
                                                toast.error(`Failed to resend invitation: ${error.message}`);
                                            }
                                        }}
                                        className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors"
                                    >
                                        Resend Invitation
                                    </button>
                                    <button
                                        onClick={async () => {
                                            if (!window.confirm(`Cancel invitation for ${user.firstName} ${user.lastName}?\n\nThis will permanently delete this pending user. This action cannot be undone.`)) {
                                                return;
                                            }
                                            try {
                                                await apiClient.cancelInvitation(user.id);
                                                toast.success('Invitation cancelled successfully.');
                                                if (onRefresh) onRefresh();
                                                onClose();
                                            } catch (error: any) {
                                                toast.error(`Failed to cancel invitation: ${error.message}`);
                                            }
                                        }}
                                        className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 transition-colors"
                                    >
                                        Cancel Invitation
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Activity History */}
                    <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 flex items-center">
                            <Activity className="w-5 h-5 mr-2" style={{ color: '#69BE28' }} />
                            Recent Activity
                        </h3>
                        <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                            {loadingActivities ? (
                                <div className="text-center py-4">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                                    <p className="text-sm text-gray-600 mt-2">Loading activity...</p>
                                </div>
                            ) : activities.length === 0 ? (
                                <p className="text-sm text-gray-600 text-center py-4">No recent activity recorded</p>
                            ) : (
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                    {activities.map((activity) => (
                                        <div
                                            key={activity.id}
                                            className="flex items-start space-x-3 p-2 bg-white rounded border border-gray-200 hover:border-gray-300 transition-colors"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-xs font-medium text-gray-900">
                                                        {formatActionType(activity.action)}
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        {formatRelativeTime(activity.timestamp)}
                                                    </span>
                                                </div>
                                                {activity.user && (
                                                    <p className="text-xs text-gray-600">
                                                        by {activity.user.firstName} {activity.user.lastName}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Quick Actions</h3>
                        <div className="flex flex-wrap gap-2">
                            {/* Edit - Always available */}
                            {onEdit && (
                                <button
                                    onClick={() => {
                                        onEdit();
                                        onClose();
                                    }}
                                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Edit User
                                </button>
                            )}

                            {/* Reset Password - Only if NOT viewing own account and user is active */}
                            {user.isActive && user.id !== currentUserId && (
                                <button
                                    onClick={() => setShowResetPassword(true)}
                                    className="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-md hover:bg-amber-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                                >
                                    Reset Password
                                </button>
                            )}

                            {/* Deactivate - Only if NOT viewing own account */}
                            {user.isActive && user.id !== currentUserId && onDeactivate && (
                                <button
                                    onClick={() => {
                                        onDeactivate();
                                        onClose();
                                    }}
                                    className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                >
                                    Deactivate User
                                </button>
                            )}

                            {/* Reactivate - Only if NOT viewing own account */}
                            {!user.isActive && user.id !== currentUserId && onReactivate && (
                                <button
                                    onClick={() => {
                                        onReactivate();
                                        onClose();
                                    }}
                                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                >
                                    Reactivate User
                                </button>
                            )}

                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Reset Password Modal */}
            {showResetPassword && (
                <ResetPasswordModal
                    user={{
                        id: user.id,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        email: user.email
                    }}
                    isOpen={showResetPassword}
                    onClose={() => setShowResetPassword(false)}
                    onSuccess={() => {
                        if (onRefresh) {
                            onRefresh();
                        }
                    }}
                />
            )}
        </div>
    );
};

export default UserViewModal;